<template>
  <component :is="allSitesMode ? 'BaseLayout' : 'AdminLayout'">
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">Utilisateurs ({{ totalRows }})</h1>
          <small class="text-muted">
            Gestion des comptes (gérants & caissiers) et droits d’accès.
          </small>
        </div>

        <div class="d-flex gap-2">
          <RouterLink
            v-if="allSitesMode"
            :to="{ name: 'Sites' }"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            title="Retour à la liste des sites"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste des sites</span>
          </RouterLink>

          <ModuleActivityButton :module="['users', 'User']" label="Utilisateurs" />

          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            :class="{ active: filtersVisible }"
            :title="filtersVisible ? 'Masquer les filtres' : 'Afficher les filtres'"
            @click="toggleFilters"
          >
            <i class="fa" :class="filtersVisible ? 'fa-eye-slash' : 'fa-filter'"></i>
            <span class="btn-label ms-1">Filtres</span>
          </button>

          <button v-if="canCreateUsers" class="btn btn-primary btn-sm btn-icon-label" @click="openCreateDialog">
            <i class="fa fa-plus"></i>
            <span class="btn-label ms-1">Nouvel utilisateur</span>
          </button>
        </div>
      </div>

      <!-- Messages globaux -->
      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>
      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <!-- ✅ FILTRES -->
      <div v-show="filtersVisible" class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <div class="row g-2 align-items-end">
            <div class="col-md-3">
              <label class="form-label small mb-1">Rôle</label>
              <select class="form-select form-select-sm" v-model="filters.role">
                <option value="">Tous</option>
                <option value="gerant">Gérant</option>
                <option value="caissier">Caissier</option>
                <option value="utilisateur">Utilisateur</option>
                <option v-if="canFilterAllRoles" value="support">Support</option>
                <option v-if="canFilterAllRoles" value="superadmin">Superadmin</option>
                <option v-if="canFilterAllRoles" value="super_admin">Superadmin</option>
                <option v-if="canFilterAllRoles" value="demo">Démo</option>
              </select>
            </div>

            <div v-if="allSitesMode" class="col-md-3">
              <label class="form-label small mb-1">Site</label>
              <PrimevueDropdown
                v-model="filters.shop_id"
                :options="siteOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Tous les sites"
                showClear
                class="w-100"
              />
            </div>

            <div class="col-md-2">
              <label class="form-label small mb-1 d-block">Actifs</label>
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="onlyActiveUsers"
                  v-model="filters.only_active"
                />
                <label class="form-check-label small" for="onlyActiveUsers">
                  Actifs uniquement
                </label>
              </div>
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm btn-icon-label"
                @click="resetFilters"
                :disabled="isLoading"
              >
                <i class="fa fa-rotate-left"></i>
                <span class="btn-label ms-1">Réinitialiser</span>
              </button>
              <button
                type="button"
                class="btn btn-primary btn-sm btn-icon-label"
                @click="applyFilters"
                :disabled="isLoading"
              >
                <i class="fa fa-search"></i>
                <span class="btn-label ms-1">Filtrer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="createdCredentials" class="card user-credentials-card mb-3">
        <div class="card-body">
          <div class="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h2 class="h6 mb-1">
                <i class="fa fa-key me-1"></i>
                Accès temporaire à partager
              </h2>
              <p class="text-muted small mb-0">
                Communiquez ces informations à l’utilisateur. Il devra changer son mot de passe à la première connexion.
              </p>
            </div>

            <div class="d-flex gap-2">
              <button
                type="button"
                class="btn btn-outline-primary btn-sm btn-icon-label"
                @click="copyCreatedCredentials"
              >
                <i class="fa fa-copy"></i>
                <span class="btn-label ms-1">Copier</span>
              </button>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm btn-action-icon"
                title="Masquer"
                aria-label="Masquer les identifiants temporaires"
                @click="createdCredentials = null"
              >
                <i class="fa fa-xmark"></i>
              </button>
            </div>
          </div>

          <div class="row g-2 mt-3">
            <div class="col-md-6">
              <label class="form-label small fw-semibold mb-1">Identifiant</label>
              <div class="credential-box">{{ createdCredentials.username }}</div>
            </div>
            <div class="col-md-6">
              <label class="form-label small fw-semibold mb-1">Mot de passe temporaire</label>
              <div class="credential-box">{{ createdCredentials.password }}</div>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="usersResponse"
        :columns="tableColumns"
        :loading="isLoading"
        :selectable="canBulkUpdateUsers"
        v-model:selected-row-ids="bulkSelectedIds"
        :is-row-selectable="canSelectUser"
        :selection-disabled="bulkActionPending"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        state-key="users.list"
        searchable
        v-model:search-value="filters.search"
        search-placeholder="Rechercher nom, email ou code..."
        :detail-route-name="allSitesMode ? 'AllSitesUserShow' : 'UserShow'"
        empty-text="Aucun utilisateur trouvé."
        @search-change="handleTableSearch"
        @sort-change="handleSortChange"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @state-restored="Object.assign(table, { page: $event.page, perPage: $event.perPage })"
        @refresh="fetchUsers"
      >
        <template #bulk-actions="{ selectedCount, isOverLimit }">
          <button type="button" class="btn btn-sm btn-outline-success" :disabled="bulkActionPending || isOverLimit" @click="bulkSetUserStatus('activate')">
            <i class="fa fa-check me-1"></i>Activer {{ selectedCount }}
          </button>
          <button type="button" class="btn btn-sm btn-outline-danger" :disabled="bulkActionPending || isOverLimit" @click="bulkSetUserStatus('deactivate')">
            <i class="fa fa-ban me-1"></i>Désactiver {{ selectedCount }}
          </button>
        </template>

        <template #cell-username="{ row }">
          <span class="text-muted small">{{ row.username || '-' }}</span>
        </template>

        <template #cell-role="{ row }">
          <span class="badge bg-secondary text-capitalize">
            {{ roleLabel(row.role) }}
          </span>
        </template>

        <template #cell-shop="{ row }">
          <span class="small">
            {{ row.shop?.name || (row.shop_id ? `Site #${row.shop_id}` : 'Tous les sites') }}
          </span>
        </template>

        <template #cell-permissions="{ row }">
          <span class="badge bg-light text-dark">
            {{ permissionCount(row) }} droit(s)
          </span>
        </template>

        <template #cell-is_active="{ row }">
          <span class="badge" :class="row.is_active ? 'bg-success' : 'bg-danger'">
            {{ row.is_active ? 'Actif' : 'Inactif' }}
          </span>
        </template>
      </BootstrapDataTable>

      <!-- Dialog création / modification -->
      <PrimevueDialog
        v-model:visible="dialogVisible"
        :modal="true"
        :closable="false"
        :style="{ width: 'min(920px, 96vw)' }"
      >
        <template #header>
          <h5 class="modal-title mb-0">
            {{ dialogMode === 'create' ? 'Nouvel utilisateur' : "Modifier l'utilisateur" }}
          </h5>
        </template>

        <UserForm
          :mode="dialogMode"
          :user="selectedUser"
          :all-sites-mode="allSitesMode"
          @saved="handleSaved"
          @cancel="closeDialog"
        />
      </PrimevueDialog>

      <PrimevueDialog
        v-if="allSitesMode"
        :visible="isDetailDialogVisible"
        :modal="true"
        :style="{ width: 'min(1120px, 96vw)' }"
        @update:visible="closeDetailDialog"
      >
        <template #header><h5 class="modal-title mb-0">Détail utilisateur</h5></template>
        <UserShowView embedded :all-sites-mode="true" />
      </PrimevueDialog>
    </template>
  </component>
</template>

<script>
import { defineComponent } from 'vue';
import UserForm from './UserForm.vue';
import UserShowView from './UserShowView.vue';
import {
  cleanFilterParams,
  countActiveFilters,
  extractApiTotal,
  extractApiList,
  loadModuleFilterState,
  saveModuleFilterValues,
  saveModuleFilterVisibility,
} from '@/support/moduleFilters';
import { canAdministerUser, hasPermission, isSuperAdminUser, isSupportUser } from '@/support/permissions';
import { bulkActionViewMixin } from '@/support/bulkActions';

const FILTER_MODULE = 'users';
const DEFAULT_FILTERS = {
  search: '',
  role: '',
  only_active: true,
  shop_id: null,
};

export default defineComponent({
  name: 'UsersView',
  mixins: [bulkActionViewMixin],
  components: { UserForm, UserShowView },
  props: {
    allSitesMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      users: [],
      usersResponse: null,
      isLoading: false,
      table: {
        page: 1,
        perPage: 10,
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'name', label: 'Nom', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'username', label: 'Code', sortable: true },
        { key: 'role', label: 'Rôle', sortable: true, class: 'text-center' },
        { key: 'shop', label: 'Site', sortable: false },
        { key: 'permissions', label: 'Droits', sortable: false, class: 'text-center' },
        { key: 'is_active', label: 'Statut', sortable: true, class: 'text-center' },
      ],

      // ✅ filtres
      filters: { ...DEFAULT_FILTERS },
      filtersVisible: false,

      dialogVisible: false,
      dialogMode: 'create',
      selectedUser: null,
      createdCredentials: null,

      errorMessage: '',
      successMessage: '',
      siteOptions: [],
    };
  },
  mounted() {
    this.restoreFilters();
    if (this.allSitesMode) this.loadSites();
    this.fetchUsers();
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    canCreateUsers() {
      return hasPermission(this.currentUser, 'users.create');
    },
    canBulkUpdateUsers() {
      return hasPermission(this.currentUser, 'users.update');
    },
    canFilterAllRoles() {
      return isSuperAdminUser(this.currentUser) || isSupportUser(this.currentUser);
    },
    isDetailDialogVisible() {
      return this.allSitesMode && this.$route?.name === 'AllSitesUserShow';
    },
    totalRows() {
      return extractApiTotal(this.usersResponse, this.users.length);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, DEFAULT_FILTERS, ['search']);
    },
  },
  watch: {
    filters: {
      deep: true,
      handler(filters) {
        saveModuleFilterValues(FILTER_MODULE, filters);
      },
    },
    filtersVisible(value) {
      saveModuleFilterVisibility(FILTER_MODULE, value);
    },
  },
  methods: {
    canSelectUser(user) {
      if (Number(user?.id || 0) === Number(this.currentUser?.id || 0)) {
        return false;
      }

      return canAdministerUser(this.currentUser, user);
    },

    async bulkSetUserStatus(action) {
      const isActivation = action === 'activate';
      const suffix = this.allSitesMode ? '?all_sites=1' : '';

      await this.performBulkAction({
        url: `/users/bulk/${action}${suffix}`,
        method: 'patch',
        itemLabel: 'utilisateur',
        confirmation: (count) => ({
          header: isActivation ? 'Activer les utilisateurs' : 'Désactiver les utilisateurs',
          message: `${isActivation ? 'Activer' : 'Désactiver'} les ${count} utilisateurs sélectionnés ?`,
          acceptLabel: `${isActivation ? 'Activer' : 'Désactiver'} ${count}`,
          acceptClass: isActivation ? 'p-button-success' : 'p-button-danger',
        }),
        refresh: () => this.fetchUsers(),
      });
    },

    restoreFilters() {
      const state = loadModuleFilterState(FILTER_MODULE, DEFAULT_FILTERS);
      this.filters = state.values;
      this.filtersVisible = state.visible;
    },

    toggleFilters() {
      this.filtersVisible = !this.filtersVisible;
    },

    async fetchUsers() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';

      try {
        const response = await this.$api.get(this.allSitesMode ? '/users/all-sites' : '/users', {
          params: {
            ...cleanFilterParams(this.filters),
            paginate: 1,
            page: this.table.page,
            per_page: this.table.perPage,
            sort_by: this.table.sortBy,
            sort_dir: this.table.sortDir,
          },
        });
        this.usersResponse = response.data;
        this.users = extractApiList(response.data);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs', error);
        this.errorMessage = "Impossible de charger la liste des utilisateurs.";
      } finally {
        this.isLoading = false;
      }
    },

    async loadSites() {
      try {
        const response = await this.$api.get('/shops', { params: { limit: 500 } });
        this.siteOptions = extractApiList(response.data).map((shop) => ({
          label: shop.name || shop.code || `Site #${shop.id}`,
          value: Number(shop.id),
        }));
      } catch (error) {
        this.siteOptions = [];
      }
    },

    resetFilters() {
      this.filters = { ...DEFAULT_FILTERS };
      this.table.page = 1;
      this.fetchUsers();
    },

    applyFilters() {
      this.table.page = 1;
      this.fetchUsers();
    },

    handleTableSearch(search) {
      this.filters.search = search;
      this.table.page = 1;
      this.fetchUsers();
    },

    openCreateDialog() {
      this.dialogMode = 'create';
      this.selectedUser = null;
      this.createdCredentials = null;
      this.dialogVisible = true;
    },

    openEditDialog(user) {
      this.dialogMode = 'edit';
      this.selectedUser = { ...user };
      this.dialogVisible = true;
    },

    roleLabel(role) {
      if (role === 'gerant') return 'Gérant';
      if (role === 'caissier') return 'Caissier';
      if (role === 'utilisateur') return 'Utilisateur';
      if (role === 'support') return 'Support';
      if (role === 'superadmin' || role === 'super_admin') return 'Superadmin';
      if (role === 'demo') return 'Démo';
      return role || '-';
    },

    permissionCount(user) {
      return Array.isArray(user.permissions) ? user.permissions.length : 0;
    },

    closeDialog() {
      this.dialogVisible = false;
      this.selectedUser = null;
    },

    async handleSaved(responsePayload) {
      const wasCreate = this.dialogMode === 'create';
      const data = responsePayload?.data || {};
      const createdUser = data.user || null;
      const temporaryPassword = data.temp_password || data.new_password || '';

      this.dialogVisible = false;
      await this.fetchUsers();

      this.successMessage = responsePayload?.message || (wasCreate
        ? (data.email_sent ? 'Utilisateur créé. Un lien de création du mot de passe a été envoyé par email.' : 'Utilisateur créé avec succès.')
        : 'Utilisateur mis à jour avec succès.');

      if (wasCreate && createdUser && temporaryPassword) {
        this.createdCredentials = {
          username: createdUser.username,
          password: temporaryPassword,
        };
      }
    },

    async confirmDelete(user) {
      const ok = await this.$confirmAction(
        `Voulez-vous vraiment supprimer l'utilisateur "${user.name}" ?`
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        if (this.allSitesMode) {
          await this.$api.delete(`/users/${user.id}`, { params: { all_sites: 1 } });
        } else {
          await this.$api.delete(`/users/${user.id}`);
        }
        this.successMessage = 'Utilisateur supprimé.';
        await this.fetchUsers();
      } catch (error) {
        console.error('Erreur lors de la suppression de l’utilisateur', error);
        this.errorMessage = this.extractErrorMessage(
          error,
          "Une erreur est survenue lors de la suppression de l'utilisateur."
        );
      }
    },

    goToShow(user) {
      this.$router.push({ name: this.allSitesMode ? 'AllSitesUserShow' : 'UserShow', params: { id: user.id } });
    },
    closeDetailDialog() {
      this.$router.push({ name: 'AllSitesUsers' });
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.table.page = 1;
      this.fetchUsers();
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchUsers();
    },

    handlePerPageChange(perPage) {
      this.table.perPage = perPage;
      this.table.page = 1;
      this.fetchUsers();
    },

    async copyCreatedCredentials() {
      if (!this.createdCredentials) return;

      const text = [
        `Identifiant : ${this.createdCredentials.username}`,
        `Mot de passe temporaire : ${this.createdCredentials.password}`,
      ].join('\n');

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.setAttribute('readonly', 'readonly');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        this.successMessage = 'Identifiant et mot de passe temporaire copiés.';
      } catch (error) {
        this.errorMessage = 'Impossible de copier les identifiants automatiquement.';
      }
    },

    extractErrorMessage(error, fallback) {
      if (error?.response?.data?.errors) {
        return Object.values(error.response.data.errors).flat().join(' ');
      }

      return fallback;
    },
  },
});
</script>

<style scoped>
.user-credentials-card {
  border-color: rgba(13, 110, 253, 0.25);
  box-shadow: 0 0.35rem 1rem rgba(13, 110, 253, 0.08);
}

.credential-box {
  background: #f8f9fa;
  border: 1px solid rgba(33, 37, 41, 0.12);
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92rem;
  font-weight: 700;
  padding: 0.6rem 0.75rem;
  word-break: break-all;
}
</style>
